import { getFromWebmFile } from '@/scripts/metadata/ebml'
import { getGltfBinaryMetadata } from '@/scripts/metadata/gltf'
import { getFromIsobmffFile } from '@/scripts/metadata/isobmff'
import { getDataFromJSON } from '@/scripts/metadata/json'
import { getMp3Metadata } from '@/scripts/metadata/mp3'
import { getOggMetadata } from '@/scripts/metadata/ogg'
import { getSvgMetadata } from '@/scripts/metadata/svg'
import type { ComfyMetadata } from '@/types/metadataTypes'
import {
  getAvifMetadata,
  getWebpMetadata,
  getFlacMetadata,
  getLatentMetadata,
  getPngMetadata
} from '@/scripts/pnginfo'

function getEffectiveType(file: File): string {
  if (file.type && file.type !== 'binary/octet-stream') return file.type

  const ext = file.name?.split('.').pop()?.toLowerCase()
  const extToMime: Record<string, string> = {
    png: 'image/png',
    avif: 'image/avif',
    webp: 'image/webp',
    mp3: 'audio/mpeg',
    ogg: 'audio/ogg',
    flac: 'audio/flac',
    webm: 'video/webm',
    mp4: 'video/mp4',
    mov: 'video/quicktime',
    m4v: 'video/x-m4v',
    svg: 'image/svg+xml',
    glb: 'model/gltf-binary',
    json: 'application/json'
  }
  return ext ? (extToMime[ext] ?? file.type) : file.type
}

export async function getWorkflowDataFromFile(
  file: File
): Promise<ComfyMetadata | Record<string, string | object> | undefined> {
  const type = getEffectiveType(file)
  if (type === 'image/png') {
    return await getPngMetadata(file)
  }
  if (type === 'image/avif') {
    return await getAvifMetadata(file)
  }
  if (type === 'image/webp') {
    const pngInfo = await getWebpMetadata(file)
    // Support loading workflows from that webp custom node.
    const workflow = pngInfo?.workflow || pngInfo?.Workflow
    const prompt = pngInfo?.prompt || pngInfo?.Prompt
    return { workflow, prompt }
  }
  if (type === 'audio/mpeg') {
    return await getMp3Metadata(file)
  }
  if (type === 'audio/ogg') {
    return await getOggMetadata(file)
  }
  if (type === 'audio/flac' || type === 'audio/x-flac') {
    const pngInfo = await getFlacMetadata(file)
    const workflow = pngInfo?.workflow || pngInfo?.Workflow
    const prompt = pngInfo?.prompt || pngInfo?.Prompt

    return { workflow, prompt }
  }
  if (type === 'video/webm') {
    return await getFromWebmFile(file)
  }
  if (
    file.name?.endsWith('.mp4') ||
    file.name?.endsWith('.mov') ||
    file.name?.endsWith('.m4v') ||
    type === 'video/mp4' ||
    type === 'video/quicktime' ||
    type === 'video/x-m4v'
  ) {
    return await getFromIsobmffFile(file)
  }
  if (type === 'image/svg+xml' || file.name?.endsWith('.svg')) {
    return await getSvgMetadata(file)
  }
  if (type === 'model/gltf-binary' || file.name?.endsWith('.glb')) {
    return await getGltfBinaryMetadata(file)
  }
  if (file.name?.endsWith('.latent') || file.name?.endsWith('.safetensors')) {
    return await getLatentMetadata(file)
  }

  if (type === 'application/json' || file.name?.endsWith('.json')) {
    return getDataFromJSON(file)
  }
  return
}
